import React, { useState, useEffect } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Pagination,
  InlineLoading,
  InlineNotification,
  Tag,
  Button,
  Dropdown,
} from "@carbon/react";
import {  Filter } from "@carbon/icons-react";
import { callMongoApi } from "../../../services/mongoApiService";
import { callApi } from "../../../services/apiService";

// Helper function to format property types as tags
const PropertyTypesTags = ({ types }) => {
  if (!types || types.length === 0) return <span>-</span>;
  return (
    <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
      {types.map((type, index) => (
        <Tag key={index} size="sm" type="blue">
          {type}
        </Tag>
      ))}
    </div>
  );
};

export default function LocationReport() {


  // Location filter states - HIERARCHY: District → Taluk → Village → Area
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [villages, setVillages] = useState([]);
  const [areas, setAreas] = useState([]);
  
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTaluk, setSelectedTaluk] = useState(null);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  
  const [loadingLocations, setLoadingLocations] = useState({
    districts: false,
    taluks: false,
    villages: false,
    areas: false
  });

  // Table data states
  const [tableData, setTableData] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Search logic
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch districts on component mount
  useEffect(() => {
    fetchDistricts();
  }, []);

  

  // Fetch Districts
  const fetchDistricts = async () => {
    setLoadingLocations(prev => ({ ...prev, districts: true }));
    try {
      const response = await callApi("GET", "/districts/");
      const districtsData = response?.data|| [];
      setDistricts(
        districtsData.map((item) => ({
          id: item.DISTRICT_ID || item.id,
          text: item.DISTRICT_NAME || item.name
        }))
      );
    } catch (error) {
      
      setError("Failed to load districts. Please try again.");
    } finally {
      setLoadingLocations(prev => ({ ...prev, districts: false }));
    }
  };

  // Fetch Taluks by District ID
  const fetchTaluks = async (districtId) => {
    setLoadingLocations(prev => ({ ...prev, taluks: true }));
    try {
      const response = await callApi("GET", `/taluk-masters/?district_id=${districtId}`);
      const taluksData = response?.data || [];
      setTaluks(
        taluksData.map((item) => ({
          id: item.id || item.id,
          text: item.name
        }))
      );
    } catch (error) {
      
      setError("Failed to load taluks. Please try again.");
    } finally {
      setLoadingLocations(prev => ({ ...prev, taluks: false }));
    }
  };

  // Fetch Villages by Taluk ID
  const fetchVillages = async (talukId) => {
    setLoadingLocations(prev => ({ ...prev, villages: true }));
    try {
      const response = await callApi("GET", `/villages/?taluk_id=${talukId}`);
      const villagesData = response?.data || [];
      setVillages(
        villagesData.map((item) => ({
          id: item.VILLAGE_ID || item.id,
          text: item.VILLAGE_NAME || item.name
        }))
      );
    } catch (error) {
      
      setError("Failed to load villages. Please try again.");
    } finally {
      setLoadingLocations(prev => ({ ...prev, villages: false }));
    }
  };

  // Fetch Areas by Village ID
  const fetchAreas = async (villageId) => {
    setLoadingLocations(prev => ({ ...prev, areas: true }));
    try {
      const response = await callApi("GET", `/areas/?village_id=${villageId}`);
      const areasData = response?.data|| [];
      setAreas(
        areasData.map((item) => ({
          id: item.AREA_ID || item.id,
          text: item.AREA_NAME || item.name
        }))
      );
    } catch (error) {
      
      setError("Failed to load areas. Please try again.");
    } finally {
      setLoadingLocations(prev => ({ ...prev, areas: false }));
    }
  };
   
const applyFilters = async () => {
  setLoading(true);
  setError("");

  try {
    const queryParams = new URLSearchParams();

    if (selectedDistrict?.text) {
      queryParams.append("district", selectedDistrict.text);
    }

    if (selectedTaluk?.text) {
      queryParams.append("taluk", selectedTaluk.text);
    }

    if (selectedVillage?.text) {
      queryParams.append("village", selectedVillage.text);
    }

    if (selectedArea?.text) {
      queryParams.append("area", selectedArea.text);
    }

    const url = `/properties/reports/location/count?${queryParams.toString()}`;

    

    const response = await callMongoApi("GET", url);
    
    
    

    const locations = response?.data?.locations || [];

    const formattedData = locations.map((item, index) => ({
      id: String(index + 1),

      district: item.location?.district || "-",
      taluk: item.location?.taluk || "-",
      village: item.location?.village || "-",
      area: item.location?.area_name || "-",

      totalProperties: item.total_properties || 0,

      propertyTypes: item.property_types || [],
    }));

    setTableData(formattedData);

    setTableHeaders([
      { key: "district", header: "District" },
      { key: "taluk", header: "Taluk" },
      { key: "village", header: "Village" },
      { key: "area", header: "Area" },
      { key: "totalProperties", header: "Total Properties" },
      { key: "propertyTypes", header: "Property Types" },
    ]);

    setPage(1);

  } catch (error) {
 

  setError(error.message);
  setTableData([]);
}
};
  // Apply search filter
  const filteredData = React.useMemo(() => {
    let filtered = [...tableData];
    
    if (debouncedSearch) {
      filtered = filtered.filter(row =>
        JSON.stringify(Object.values(row))
          .toLowerCase()
          .includes(debouncedSearch)
      );
    }
    
    return filtered;
  }, [tableData, debouncedSearch]);

  const paginatedData = React.useMemo(() =>
    filteredData.slice((page - 1) * pageSize, page * pageSize),
    [filteredData, page, pageSize]
  );

  // Clear all location filters
  const clearAllFilters = () => {
    setSelectedDistrict(null);
    setSelectedTaluk(null);
    setSelectedVillage(null);
    setSelectedArea(null);
    setTableData([]);
    setSearchTerm("");
    setPage(1);
  };

  // Check if any filter is active
  const hasActiveFilters = selectedDistrict || selectedTaluk || selectedVillage || selectedArea;

  // Get current level title
  const getCurrentLevelTitle = () => {
    if (selectedArea) return `Area - ${selectedArea.text}`;
    if (selectedVillage) return `Village  - ${selectedVillage.text}`;
    if (selectedTaluk) return `Taluk  - ${selectedTaluk.text}`;
    if (selectedDistrict) return "District ";
    return "Location Report";
  };

  return (
    <>
      {/* Location Filters Bar */}
            <div
  style={{
    padding: "1rem",
    backgroundColor: "#f4f4f4",
    borderRadius: "8px",
    marginBottom: "1rem",
    border: "1px solid #e0e0e0",
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginBottom: "1rem",
      gap: "0.5rem",
    }}
  >
    <Filter />

    <h4 style={{ margin: 0 }}>
     Filter Location 
    </h4>
  </div>

  <div
    style={{
      display: "flex",
      gap: "1rem",
      alignItems: "flex-end",
      flexWrap: "wrap",
    }}
  >
          <div style={{ minWidth: "200px" }}>
            <Dropdown
              id="district-filter"
              titleText="District"
              label={loadingLocations.districts ? "Loading..." : "Select district"}
              items={districts}
              itemToString={(item) => item?.text || ""}
              selectedItem={selectedDistrict}
onChange={({ selectedItem }) => {
  setSelectedDistrict(selectedItem);

  setSelectedTaluk(null);
  setSelectedVillage(null);
  setSelectedArea(null);

  setTaluks([]);
  setVillages([]);
  setAreas([]);

  if (selectedItem) {
    fetchTaluks(selectedItem.id);
  }
}}              disabled={loadingLocations.districts}
            />
          </div>
          
          <div style={{ minWidth: "200px" }}>
            <Dropdown
              id="taluk-filter"
              titleText="Taluk"
              label={loadingLocations.taluks ? "Loading..." : (selectedDistrict ? "Select taluk" : "Select district first")}
              items={taluks}
              itemToString={(item) => item?.text || ""}
              selectedItem={selectedTaluk}
onChange={({ selectedItem }) => {
  setSelectedTaluk(selectedItem);

  setSelectedVillage(null);
  setSelectedArea(null);

  setVillages([]);
  setAreas([]);

  if (selectedItem) {
    fetchVillages(selectedItem.id);
  }
}}              disabled={!selectedDistrict || loadingLocations.taluks}
            />
          </div>
          
          <div style={{ minWidth: "200px" }}>
            <Dropdown
              id="village-filter"
              titleText="Village"
              label={loadingLocations.villages ? "Loading..." : (selectedTaluk ? "Select village" : "Select taluk first")}
              items={villages}
              itemToString={(item) => item?.text || ""}
              selectedItem={selectedVillage}
onChange={({ selectedItem }) => {
  setSelectedVillage(selectedItem);

  setSelectedArea(null);
  setAreas([]);

  if (selectedItem) {
    fetchAreas(selectedItem.id);
  }
}}              disabled={!selectedTaluk || loadingLocations.villages}
            />
          </div>
          
          <div style={{ minWidth: "200px" }}>
            <Dropdown
              id="area-filter"
              titleText="Area"
              label={loadingLocations.areas ? "Loading..." : (selectedVillage ? "Select area" : "Select village first")}
              items={areas}
              itemToString={(item) => item?.text || ""}
              selectedItem={selectedArea}
onChange={({ selectedItem }) => {
  setSelectedArea(selectedItem);
}}              disabled={!selectedVillage || loadingLocations.areas}
            />
          </div>
          
          {hasActiveFilters && (
         
         <div
  style={{
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "1rem",
    gap: "0.75rem",
  }}
>
  <Button kind="secondary" onClick={clearAllFilters}>
    Reset
  </Button>

  <Button onClick={applyFilters}>
    Search
  </Button>
</div>
          )}
        </div>
        <div
  style={{
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "1rem",
  }}
>
  {selectedDistrict && (
    <Tag
      type="blue"
      filter
      onClose={() => setSelectedDistrict(null)}
    >
      District: {selectedDistrict.text}
    </Tag>
  )}

  {selectedTaluk && (
    <Tag
      type="purple"
      filter
      onClose={() => setSelectedTaluk(null)}
    >
      Taluk: {selectedTaluk.text}
    </Tag>
  )}

  {selectedVillage && (
    <Tag
      type="green"
      filter
      onClose={() => setSelectedVillage(null)}
    >
      Village: {selectedVillage.text}
    </Tag>
  )}

  {selectedArea && (
    <Tag
      type="cyan"
      filter
      onClose={() => setSelectedArea(null)}
    >
      Area: {selectedArea.text}
    </Tag>
  )}
</div>
      </div>

      {/* Data Table for Summary Statistics */}
      <DataTable rows={paginatedData} headers={tableHeaders}>
        {({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => {
          return (
            <TableContainer title={getCurrentLevelTitle()}>
              <TableToolbar>
                <TableToolbarContent>
                  <TableToolbarSearch
                    expanded
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    value={searchTerm}
                  />
                </TableToolbarContent>
              </TableToolbar>
              
              {loading && (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <InlineLoading description="Loading reports data..." />
                </div>
              )}

              {error && !loading && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={error}
                    lowContrast
                    onClose={() => setError("")}
                  />
                </div>
              )}

              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => (
                      <TableHeader key={header.key} {...getHeaderProps({ header })}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading && paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length} style={{ textAlign: 'center', padding: '2rem' }}>
                        {!hasActiveFilters
                          ? 'Select a district to view summary statistics'
                          : error || `No data found for the selected ${selectedArea ? 'area' : selectedVillage ? 'village' : selectedTaluk ? 'taluk' : 'location'}`}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row, rowIndex) => (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        {headers.map((header) => (
                          <TableCell key={header.key}>
                            {header.key === 'propertyTypes' ? (
                              <PropertyTypesTags types={row[header.key]} />
                            ) : (
                              row[header.key]
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {/* Pagination */}
      {filteredData.length > 0 && !loading && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            totalItems={filteredData.length}
            page={page}
            pageSize={pageSize}
            pageSizes={[100, 50, 20, 10, 5]}
            onChange={({ page: p, pageSize: ps }) => { 
              setPage(p); 
              setPageSize(ps); 
            }}
          />
        </div>
      )}
    </>
  );
}